import Phaser from 'phaser';
import { BootScene } from '@/scenes/BootScene';
import { UnderCityScene } from '@/scenes/UnderCityScene';
import { GAME_WIDTH, GAME_HEIGHT } from '@/utils/constants';
import { ArcRegistry } from '@/core/ArcRegistry';
import { GameState } from '@/core/GameState';
import { SettingsStore, LocalStorageSettingsBackend } from '@/core/SettingsStore';
import { SaveManager } from '@/core/SaveManager';
import { LocalStorageBackend } from '@/systems/SaveSerializer';
import { SERVICES_KEY } from '@/core/GameServices';
import type { GameServices } from '@/core/GameServices';
import { locale } from '@/core/Locale';
import { UNDERCITY_ARC } from '@/arcs/undercity.arc';

const INITIAL_BLOB_MASS = 0.2;

export function createBiomassGame(): Phaser.Game {
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent: 'game-container',
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    backgroundColor: '#050505',
    pixelArt: true,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: false,
      },
    },
    scene: [BootScene, UnderCityScene],
    input: {
      gamepad: true,
    },
  };

  const game = new Phaser.Game(config);

  void bootServices(game);

  if (import.meta.env.DEV) {
    (window as unknown as { __BIOMASS__: Phaser.Game }).__BIOMASS__ = game;
  }
  return game;
}

async function bootServices(game: Phaser.Game): Promise<void> {
  const arcs = new ArcRegistry();
  arcs.register(UNDERCITY_ARC);

  const settings = new SettingsStore(new LocalStorageSettingsBackend());
  await settings.load();

  // Persist settings whenever they change
  settings.onChange(() => {
    void settings.save();
  });

  const manifest = UNDERCITY_ARC;
  locale.register(manifest.localeBundle);

  const state = new GameState({
    initialMass: INITIAL_BLOB_MASS,
    relationOverrides: manifest.relationOverrides,
    abilityUnlockRules: manifest.abilityUnlockRules,
    consumption: {
      brainQualityFor: (sp) => manifest.brainQualityFor?.(sp) ?? 'vermin',
      // Meals take ~2s minimum plus a mass-scaled extension. A carcass
      // (5.5 mass) pulls ~8s. Tech-tree upgrades will shave this.
      consumeDurationMs: (m) => 1800 + m * 1200,
    },
  });

  const saveManager = new SaveManager({
    backend: new LocalStorageBackend('biomass:save:'),
    bus: state.bus,
    state,
    isHardcore: () => settings.snapshot.gameplay.preferredDifficulty === 'hardcore',
  });
  saveManager.start();

  const services: GameServices = { state, settings, saveManager, arcs };
  game.registry.set(SERVICES_KEY, services);

  state.bus.emit('arc:enter', { arcId: manifest.id });

  if (import.meta.env.DEV) {
    (window as unknown as { __SERVICES__: GameServices }).__SERVICES__ = services;
  }
}
